import { useRef, useState } from 'react';
import { Award, Camera, Loader2 } from 'lucide-react';

interface CompetitionImageProps {
  imageUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
  editable?: boolean;
  onImageSelected?: (base64: string) => Promise<void>;
}

const SIZE_CLASSES = {
  sm: 'w-12 h-12 rounded-xl',
  md: 'w-14 h-14 rounded-2xl',
  lg: 'w-20 h-20 rounded-2xl',
};

const ICON_SIZES = {
  sm: 'w-6 h-6',
  md: 'w-7 h-7',
  lg: 'w-10 h-10',
};

/** Tournament photo with the same upload UX as RankingGroupImage (Award icon). */
export function CompetitionImage({
  imageUrl,
  size = 'sm',
  editable = false,
  onImageSelected,
}: CompetitionImageProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const sizeClass = SIZE_CLASSES[size];
  const iconClass = ICON_SIZES[size];

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onImageSelected) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = async () => {
      setIsUploading(true);
      try {
        await onImageSelected(reader.result as string);
      } finally {
        setIsUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const placeholder = (
    <div className={`${sizeClass} bg-gradient-to-br from-turquoise/30 to-coral/30 flex items-center justify-center shrink-0`}>
      <Award className={`${iconClass} text-white/80`} />
    </div>
  );

  if (!editable) {
    return imageUrl ? (
      <img src={imageUrl} alt="Torneio" className={`${sizeClass} object-cover shrink-0`} />
    ) : (
      placeholder
    );
  }

  return (
    <button
      type="button"
      onClick={() => fileInputRef.current?.click()}
      disabled={isUploading}
      className={`${sizeClass} relative group shrink-0 overflow-hidden`}
    >
      {imageUrl ? (
        <img src={imageUrl} alt="Torneio" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-turquoise/30 to-coral/30 flex items-center justify-center">
          <Award className={`${iconClass} text-white/80`} />
        </div>
      )}
      {(isUploading || !imageUrl) && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          {isUploading ? (
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          ) : (
            <Camera className="w-5 h-5 text-white" />
          )}
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </button>
  );
}
